import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Download,
  FileSpreadsheet,
  FileText,
  Users,
  Lightbulb,
  BarChart3,
  Activity,
  Calendar,
  Filter,
  Loader2,
  CheckCircle,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  exportDashboardData,
  exportToCSV,
  ExportData,
  ExportOptions,
} from "@/lib/exportUtils";

interface ExportModalProps {
  data: ExportData;
  filters?: {
    timeRange: string;
    userType: string;
    projectStatus: string;
    searchTerm: string;
  };
  trigger?: React.ReactNode;
}

const ExportModal: React.FC<ExportModalProps> = ({
  data,
  filters = {
    timeRange: "all",
    userType: "all",
    projectStatus: "all",
    searchTerm: "",
  },
  trigger,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<"excel" | "csv">("excel");
  const [selectedData, setSelectedData] = useState({
    users: true,
    campaigns: true,
    analytics: true,
    activity: true,
  });

  const dataCounts = {
    users: data.users?.length || 0,
    campaigns: data.campaigns?.length || 0,
    analytics: {
      userGrowth: data.analytics?.userGrowth?.length || 0,
      campaignGrowth: data.analytics?.campaignGrowth?.length || 0,
      engagement: data.analytics?.engagement?.length || 0,
    },
    activity: data.activity?.length || 0,
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `admin-dashboard-${timestamp}`;

      if (exportFormat === "excel") {
        const exportOptions: ExportOptions = {
          filename,
          includeUsers: selectedData.users,
          includeCampaigns: selectedData.campaigns,
          includeAnalytics: selectedData.analytics,
          includeActivity: selectedData.activity,
        };

        exportDashboardData(data, exportOptions);
      } else {
        // For CSV, we'll export each selected data type as a separate file
        if (selectedData.users && data.users) {
          exportToCSV(data.users, `${filename}-users`);
        }
        if (selectedData.campaigns && data.campaigns) {
          exportToCSV(data.campaigns, `${filename}-campaigns`);
        }
        if (selectedData.analytics && data.analytics?.userGrowth) {
          exportToCSV(data.analytics.userGrowth, `${filename}-user-growth`);
        }
        if (selectedData.analytics && data.analytics?.campaignGrowth) {
          exportToCSV(
            data.analytics.campaignGrowth,
            `${filename}-campaign-growth`
          );
        }
        if (selectedData.analytics && data.analytics?.engagement) {
          exportToCSV(data.analytics.engagement, `${filename}-engagement`);
        }
        if (selectedData.activity && data.activity) {
          exportToCSV(data.activity, `${filename}-activity`);
        }
      }

      toast({
        title: "Export successful! 📊",
        description: `Your data has been exported as ${exportFormat.toUpperCase()} file(s).`,
      });

      setIsOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export failed",
        description:
          "There was an error exporting your data. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.timeRange !== "all") count++;
    if (filters.userType !== "all") count++;
    if (filters.projectStatus !== "all") count++;
    if (filters.searchTerm) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="w-5 h-5" />
            Export Dashboard Data
          </DialogTitle>
          <DialogDescription>
            Select the data you want to export and choose your preferred format.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Export Format Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={exportFormat === "excel" ? "default" : "outline"}
                onClick={() => setExportFormat("excel")}
                className="justify-start"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel (.xlsx)
                <Badge variant="secondary" className="ml-auto">
                  Multi-sheet
                </Badge>
              </Button>
              <Button
                variant={exportFormat === "csv" ? "default" : "outline"}
                onClick={() => setExportFormat("csv")}
                className="justify-start"
              >
                <FileText className="w-4 h-4 mr-2" />
                CSV (.csv)
                <Badge variant="secondary" className="ml-auto">
                  Separate files
                </Badge>
              </Button>
            </div>
          </div>

          <Separator />

          {/* Data Selection */}
          <div className="space-y-4">
            <Label className="text-sm font-medium">Select Data to Export</Label>

            {/* Users Data */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <Checkbox
                id="users"
                checked={selectedData.users}
                onCheckedChange={(checked) =>
                  setSelectedData((prev) => ({
                    ...prev,
                    users: checked as boolean,
                  }))
                }
              />
              <div className="flex-1">
                <Label
                  htmlFor="users"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Users className="w-4 h-4 text-blue-500" />
                  User Data
                </Label>
                <p className="text-sm text-muted-foreground">
                  User profiles, engagement metrics, and activity
                </p>
              </div>
              <Badge variant="outline">{dataCounts.users} users</Badge>
            </div>

            {/* Campaigns Data */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <Checkbox
                id="campaigns"
                checked={selectedData.campaigns}
                onCheckedChange={(checked) =>
                  setSelectedData((prev) => ({
                    ...prev,
                    campaigns: checked as boolean,
                  }))
                }
              />
              <div className="flex-1">
                <Label
                  htmlFor="campaigns"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Lightbulb className="w-4 h-4 text-purple-500" />
                  Campaign Data
                </Label>
                <p className="text-sm text-muted-foreground">
                  Campaign details, responses, and performance metrics
                </p>
              </div>
              <Badge variant="outline">{dataCounts.campaigns} campaigns</Badge>
            </div>

            {/* Analytics Data */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <Checkbox
                id="analytics"
                checked={selectedData.analytics}
                onCheckedChange={(checked) =>
                  setSelectedData((prev) => ({
                    ...prev,
                    analytics: checked as boolean,
                  }))
                }
              />
              <div className="flex-1">
                <Label
                  htmlFor="analytics"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <BarChart3 className="w-4 h-4 text-green-500" />
                  Analytics Data
                </Label>
                <p className="text-sm text-muted-foreground">
                  Growth trends, engagement metrics, and conversion data
                </p>
              </div>
              <Badge variant="outline">
                {dataCounts.analytics.userGrowth +
                  dataCounts.analytics.campaignGrowth +
                  dataCounts.analytics.engagement}{" "}
                datasets
              </Badge>
            </div>

            {/* Activity Data */}
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <Checkbox
                id="activity"
                checked={selectedData.activity}
                onCheckedChange={(checked) =>
                  setSelectedData((prev) => ({
                    ...prev,
                    activity: checked as boolean,
                  }))
                }
              />
              <div className="flex-1">
                <Label
                  htmlFor="activity"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <Activity className="w-4 h-4 text-orange-500" />
                  Activity Log
                </Label>
                <p className="text-sm text-muted-foreground">
                  Recent platform activities and user actions
                </p>
              </div>
              <Badge variant="outline">{dataCounts.activity} activities</Badge>
            </div>
          </div>

          {/* Filters Info */}
          {activeFiltersCount > 0 && (
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Active Filters</span>
                <Badge variant="secondary">{activeFiltersCount}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Export will include only data matching your current filters.
              </p>
            </div>
          )}

          {/* Export Summary */}
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                Export Summary
              </span>
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>• Format: {exportFormat.toUpperCase()}</p>
              <p>
                • Data types:{" "}
                {Object.values(selectedData).filter(Boolean).length} selected
              </p>
              <p>
                • Total records:{" "}
                {(selectedData.users ? dataCounts.users : 0) +
                  (selectedData.campaigns ? dataCounts.campaigns : 0) +
                  (selectedData.activity ? dataCounts.activity : 0) +
                  (selectedData.analytics
                    ? dataCounts.analytics.userGrowth +
                      dataCounts.analytics.campaignGrowth +
                      dataCounts.analytics.engagement
                    : 0)}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={
                isExporting || !Object.values(selectedData).some(Boolean)
              }
              className="bg-primary hover:bg-primary/90"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportModal;
